import { type ApiResponse } from "apisauce"
import { useCallback, useEffect, useMemo, useState } from "react"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { getUser } from "../../Settings/api/services.ts"
import { getUsers } from "../../TeamManagementPage/api/services.ts"
import { getProjectTypes } from "../../Templates/services/project-types.services.ts"
import {
  type CompanyInfoLike,
  isCompanyInfoComplete
} from "../content/onboarding.ts"

export interface OnboardingSignals {
  /** null = still loading, or the check failed — never rendered as "incomplete". */
  hasTeammates: boolean | null
  hasProjectTypes: boolean | null
  hasCompanyInfo: boolean | null
}

const NOT_LOADED: OnboardingSignals = {
  hasTeammates: null,
  hasProjectTypes: null,
  hasCompanyInfo: null
}

const countAbove = <T extends { count?: number } | null | undefined>(
  response: ApiResponse<T, unknown>,
  threshold: number
): boolean | null => {
  if (!response.ok || !response.data) return null
  return (response.data.count ?? 0) > threshold
}

/**
 * Cheap, count-only reads (page_size: 1) behind the setup ladder — no new
 * backend surface, and a failed check reads as unknown rather than
 * incomplete so nothing ever lies about state it couldn't confirm.
 * `hasCompanyInfo` is the one exception that isn't a count: it reads the
 * same `getUser` endpoint Settings' own Company & System page already
 * calls, reduced through the exact required-field rule that page's form
 * declares (isCompanyInfoComplete). `refetch` lets the Setup Assistant
 * re-check real state after the user saves on the real page without a
 * full remount — see GuideOnboardingAssistantContext's poll-while-active.
 */
export const useOnboardingSignals = (
  userId?: string | null
): OnboardingSignals & {
  loaded: boolean
  refetch: () => void
  /** The raw fields behind `hasCompanyInfo`, for a panel that wants to show
   * which specific piece is still missing rather than one pass/fail flag. */
  companyInfo: CompanyInfoLike | null
} => {
  const [signals, setSignals] = useState<OnboardingSignals>(NOT_LOADED)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoLike | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false

    Promise.all([
      getUsers({ page_size: 1 }),
      getProjectTypes({ page_size: 1 }),
      userId ? getUser(userId) : Promise.resolve(null)
    ]).then(([usersRes, typesRes, userRes]) => {
      if (cancelled) return

      let hasCompanyInfo: boolean | null = null
      let company: CompanyInfoLike | null = null
      if (userRes) {
        company = userRes.ok
          ? (unwrapEnvelope<{ company_details?: CompanyInfoLike }>(userRes.data)
              ?.company_details ?? null)
          : null
        hasCompanyInfo = userRes.ok ? isCompanyInfoComplete(company) : null
      }

      setSignals({
        // The signed-in admin is already one user, so "added your team"
        // needs at least a second.
        hasTeammates: countAbove(usersRes, 1),
        hasProjectTypes: countAbove(typesRes, 0),
        hasCompanyInfo
      })
      setCompanyInfo(company)
      setLoaded(true)
    })

    return () => {
      cancelled = true
    }
  }, [userId, nonce])

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  // Memoized so this hook's return identity only changes when something it
  // actually holds changes — it feeds `GuideOnboardingAssistantContext`'s
  // own `value` memo (that provider is mounted app-wide), and a fresh
  // object literal here on every render defeated that memo on every route
  // change, not just a real signals update.
  return useMemo(
    () => ({ ...signals, loaded, refetch, companyInfo }),
    [signals, loaded, refetch, companyInfo]
  )
}
